import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, ChevronRight, ChevronDown, Zap, Bell, Cloud, CircleCheckBig, Settings2, Search, Sparkles, Pencil, ListChecks, Upload, CalendarDays, ClipboardList, Package, FileDown, Archive, Undo2, ChevronsUp, ChevronsDown } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from "recharts";
import { toast } from "sonner";
import { openBrandedPrintWindow } from "@/lib/documentTemplate";
import PlanningTab from "./project/PlanningTab";
import DrawingsTab from "./project/DrawingsTab";
import ScheduleTab from "./project/ScheduleTab";
import WorkListTab from "./project/WorkListTab";
import ProgressReportTab from "./project/ProgressReportTab";
import TimelineTab from "./project/TimelineTab";
import SnagsTab from "./project/SnagsTab";
import MaterialsTab from "./project/MaterialsTab";
import ContractsTab from "./project/ContractsTab";
import FinancingTab from "./project/FinancingTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { key: "recce", label: "Recce" },
  { key: "design", label: "Design" },
  { key: "boq", label: "BOQ" },
  { key: "order", label: "Order" },
  { key: "work_progress", label: "Work Progress" },
  { key: "snag", label: "Snag" },
  { key: "finance", label: "Finance" },
  { key: "handover", label: "Handover" },
];

const SUB_TABS = ["Scope Update","Planning","Timeline","Snag","Drawings","Schedule","Work List","Progress Report","Materials","Contracts","Financing"];

const STATUS_COLORS = {
  "Draft": "bg-slate-100 text-slate-700 border-slate-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Approved": "bg-blue-50 text-blue-700 border-blue-200",
};

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}); } catch { return s || "—"; } };

function StageStepper({ current, stages }) {
  const currentIdx = stages.findIndex(s => s.key === current);
  return (
    <div className="flex items-center gap-1 overflow-x-auto py-4 px-6 border-b border-border/50">
      {stages.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={s.key} className="flex items-center gap-1 shrink-0">
            <div className="flex items-center gap-2 px-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${done ? "bg-emerald-500 text-white" : active ? "bg-orange-500 text-white ring-4 ring-orange-500/20" : "bg-muted text-muted-foreground border border-border"}`}>
                {done ? <CircleCheckBig className="w-3.5 h-3.5" /> : i+1}
              </div>
              <span className={`text-sm ${active ? "font-semibold text-foreground" : done ? "text-emerald-600" : "text-muted-foreground"}`}>{s.label}</span>
            </div>
            {i < stages.length - 1 && (
              <div className={`w-8 border-t-2 border-dashed ${done ? "border-emerald-400" : "border-border"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function MetricRow({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`text-sm font-medium ${muted ? "text-muted-foreground" : ""}`}>{value}</div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [subTab, setSubTab] = useState("Scope Update");
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [stageNote, setStageNote] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [stageHistory, setStageHistory] = useState([]);

  const canRollback = ["super_admin","director"].includes(user?.role);
  const canArchive = ["super_admin","director"].includes(user?.role);
  const isArchived = project?.is_archived;

  const advanceStage = async () => {
    try {
      await api.post(`/projects/${id}/stage/advance`, { note: stageNote });
      toast.success("Stage advanced");
      setAdvanceOpen(false); setStageNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const rollbackStage = async () => {
    try {
      await api.post(`/projects/${id}/stage/rollback`, { note: stageNote });
      toast.success("Stage rolled back");
      setRollbackOpen(false); setStageNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openHistory = async () => {
    try { const { data } = await api.get(`/projects/${id}/stage-history`); setStageHistory(data); setHistoryOpen(true); }
    catch { toast.error("Failed to load history"); }
  };
  const archiveProject = async () => {
    if (!window.confirm(isArchived ? "Unarchive this project?" : "Archive this project?")) return;
    try {
      await api.post(`/projects/${id}/${isArchived ? "unarchive" : "archive"}`);
      toast.success(isArchived ? "Project unarchived" : "Project archived");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const exportProject = async () => {
    try {
      const { data } = await api.get(`/projects/${id}/export`);
      const rows = (arr, mapper) => arr.map(mapper).join("");
      const body = `
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px">
          <div><h1 style="margin:0">${data.project.name}</h1>
            <div style="color:#73706A;font-size:11px;margin-top:6px">${data.project.code || ""} · ${data.project.project_type || "Architecture"}${data.client ? " · Client: " + data.client.name : ""}</div>
            <div style="color:#73706A;font-size:11px">${data.project.location || ""}</div>
            <div style="margin-top:10px">
              <span style="display:inline-block;padding:2px 10px;border-radius:99px;background:#F0EFEB;font-size:10px;letter-spacing:1px;text-transform:uppercase">Stage: ${(data.project.workflow_stage||"recce").replace(/_/g," ")}</span>
              <span style="display:inline-block;padding:2px 10px;border-radius:99px;background:#F0EFEB;font-size:10px;letter-spacing:1px;text-transform:uppercase;margin-left:4px">Progress: ${data.project.progress}%</span>
            </div>
          </div>
          <div style="text-align:right;color:#73706A;font-size:10px">By ${data.generated_by}</div>
        </div>
        ${data.project.goals ? `<h2>Goals</h2><p>${data.project.goals}</p>` : ""}
        ${data.project.description ? `<h2>Description</h2><p>${data.project.description}</p>` : ""}
        <h2>Phases (${data.phases.length})</h2>
        <table><thead><tr><th>#</th><th>Name</th><th>Start</th><th>End</th><th>Progress</th><th>Status</th></tr></thead><tbody>
          ${rows(data.phases, (p,i)=>`<tr><td>${p.sequence||i+1}</td><td>${p.name}</td><td>${p.start_date||""}</td><td>${p.end_date||""}</td><td>${p.progress||0}%</td><td>${p.status||""}</td></tr>`)}
        </tbody></table>
        <h2>Milestones (${data.milestones.length})</h2>
        <table><thead><tr><th>Title</th><th>Target</th><th>Status</th></tr></thead><tbody>${rows(data.milestones, m=>`<tr><td>${m.title}</td><td>${m.target_date||""}</td><td>${m.status}</td></tr>`)}</tbody></table>
        <h2>Deliverables (${data.deliverables.length})</h2>
        <table><thead><tr><th>Title</th><th>Type</th><th>Due</th><th>Status</th></tr></thead><tbody>${rows(data.deliverables, d=>`<tr><td>${d.title}</td><td>${d.type}</td><td>${d.due_date||""}</td><td>${d.status}</td></tr>`)}</tbody></table>
        <h2>Risks (${data.risks.length})</h2>
        <table><thead><tr><th>Title</th><th>Severity</th><th>Probability</th><th>Mitigation</th><th>Status</th></tr></thead><tbody>${rows(data.risks, r=>`<tr><td>${r.title}</td><td>${r.severity}</td><td>${r.probability}</td><td>${r.mitigation||""}</td><td>${r.status}</td></tr>`)}</tbody></table>
        <h2>Work List (${data.works.length})</h2>
        <table><thead><tr><th>Title</th><th>Category</th><th>Priority</th><th>Status</th><th>Due</th><th>Progress</th></tr></thead><tbody>${rows(data.works, w=>`<tr><td>${w.title}</td><td>${w.category}</td><td>${w.priority}</td><td>${w.status}</td><td>${w.due_date||""}</td><td>${w.progress||0}%</td></tr>`)}</tbody></table>
        <h2>Drawings (${data.drawings.length})</h2>
        <table><thead><tr><th>Title</th><th>Number</th><th>Discipline</th><th>Version</th><th>Status</th></tr></thead><tbody>${rows(data.drawings, d=>`<tr><td>${d.title}</td><td>${d.drawing_number||""}</td><td>${d.discipline}</td><td>v${d.current_version||1}</td><td>${d.status}</td></tr>`)}</tbody></table>
        <h2>Stage History</h2>
        <table><thead><tr><th>Date</th><th>Action</th><th>From → To</th><th>By</th><th>Note</th></tr></thead><tbody>${rows(data.stage_history, s=>`<tr><td>${new Date(s.created_at).toLocaleDateString()}</td><td>${s.action}</td><td>${s.from_stage} → ${s.to_stage}</td><td>${s.user_name}</td><td>${s.note||""}</td></tr>`)}</tbody></table>
      `;
      openBrandedPrintWindow(body, {
        subject: `Project Report — ${data.project.name}`,
        title: "Project Report",
        number: data.project.code || "",
        date: new Date(data.generated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }),
      });
      toast.success("Export ready — print dialog opened");
    } catch { toast.error("Export failed"); }
  };
  const [activeSection, setActiveSection] = useState("all");
  const [filters, setFilters] = useState({ q: "", category: "all", status: "all", item_type: "all", uom: "all", progress: "all" });

  const load = async () => {
    try {
      const [p, s, sc] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/summary`),
        api.get(`/projects/${id}/scope-items`),
      ]);
      setProject(p.data); setSummary(s.data); setItems(sc.data);
    } catch (e) {
      toast.error("Failed to load project");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const sections = useMemo(() => {
    const map = {};
    items.forEach(it => { const s = it.section || "Unsectioned"; map[s] = (map[s]||0) + 1; });
    return Object.entries(map).map(([name, count]) => ({ name, count }));
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(it => {
      if (activeSection !== "all" && (it.section || "Unsectioned") !== activeSection) return false;
      if (filters.q && !`${it.name} ${it.description||""} ${it.code||""}`.toLowerCase().includes(filters.q.toLowerCase())) return false;
      if (filters.category !== "all" && it.category !== filters.category) return false;
      if (filters.status !== "all" && it.status !== filters.status) return false;
      if (filters.uom !== "all" && it.uom !== filters.uom) return false;
      if (filters.progress !== "all") {
        const p = it.progress || 0;
        if (filters.progress === "0" && p !== 0) return false;
        if (filters.progress === "1-49" && !(p > 0 && p < 50)) return false;
        if (filters.progress === "50-99" && !(p >= 50 && p < 100)) return false;
        if (filters.progress === "100" && p !== 100) return false;
      }
      return true;
    });
  }, [items, filters, activeSection]);

  const unique = (k) => Array.from(new Set(items.map(i => i[k]).filter(Boolean)));

  const updateProgress = async (item, progress) => {
    await api.patch(`/projects/${id}/scope-items/${item.id}`, { progress });
    load();
  };

  if (!project || !summary) return <div className="text-muted-foreground p-6">Loading project…</div>;

  const scopeStatus = summary.scope_progress >= 100 ? "Completed"
    : summary.scope_progress >= 80 ? "On track"
    : `Delayed by ${Math.max(1, Math.round((100 - summary.scope_progress)*4))}d`;

  return (
    <div className="space-y-4" data-testid="project-detail">
      {/* Header */}
      <div className="rounded-2xl bg-card border border-border/60">
        <div className="flex items-center gap-4 px-6 py-4 flex-wrap">
          <Link to="/projects" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft className="w-4 h-4" />All Projects</Link>
          <ChevronRight className="w-3 h-3 text-muted-foreground" />
          <div>
            <div className="font-display text-2xl font-semibold" data-testid="project-name">{project.name}</div>
          </div>
          <div className="text-xs px-2.5 py-1 rounded-md bg-muted/60 text-muted-foreground font-medium tracking-wide">
            JOB ID : <span className="text-foreground font-semibold">{project.code || "—"}</span>
          </div>
          <div className="ml-auto flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
            <span className="text-xs uppercase tracking-[0.15em]">Project Status :</span>
            <span className="text-primary font-medium">{(project.status||"").replace("_"," ").replace(/\b\w/g,c=>c.toUpperCase())}</span>
            {isArchived && <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid="archived-badge">Archived</span>}
            <span className="mx-1 text-border">|</span>
            <button onClick={openHistory} className="p-1.5 rounded hover:bg-muted" title="Stage History" data-testid="stage-history-btn"><Sparkles className="w-4 h-4 text-primary" /></button>
            <button onClick={exportProject} className="p-1.5 rounded hover:bg-muted" title="Export PDF" data-testid="project-export-btn"><Cloud className="w-4 h-4" /></button>
            <button className="p-1.5 rounded hover:bg-muted" title="Notifications"><Bell className="w-4 h-4" /></button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-orange-600 hover:bg-orange-700 text-white h-9" data-testid="project-actions-btn"><Zap className="w-4 h-4 mr-1.5" />Actions<ChevronDown className="w-3 h-3 ml-1" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="actions-menu">
                <DropdownMenuLabel>Project Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/projects")} data-testid="action-edit-project"><Pencil className="w-3.5 h-3.5 mr-2" /> Edit Project</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSubTab("Planning")} data-testid="action-add-planning"><ListChecks className="w-3.5 h-3.5 mr-2" /> Add Planning Item</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSubTab("Drawings")} data-testid="action-upload-drawing"><Upload className="w-3.5 h-3.5 mr-2" /> Upload Drawing</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSubTab("Schedule")} data-testid="action-add-schedule"><CalendarDays className="w-3.5 h-3.5 mr-2" /> View / Add Schedule</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSubTab("Work List")} data-testid="action-add-work"><ClipboardList className="w-3.5 h-3.5 mr-2" /> Add Work Item</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSubTab("Materials")} data-testid="action-add-material"><Package className="w-3.5 h-3.5 mr-2" /> Materials (MRL)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSubTab("Progress Report")} data-testid="action-generate-dpr"><Zap className="w-3.5 h-3.5 mr-2" /> Generate DPR</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={exportProject} data-testid="action-export"><FileDown className="w-3.5 h-3.5 mr-2" /> Export Project (PDF)</DropdownMenuItem>
                {canArchive && <DropdownMenuItem onClick={archiveProject} data-testid="action-archive"><Archive className="w-3.5 h-3.5 mr-2" /> {isArchived ? "Unarchive" : "Archive"} Project</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setAdvanceOpen(true)} data-testid="action-advance-stage"><ChevronsUp className="w-3.5 h-3.5 mr-2" /> Advance Workflow Stage</DropdownMenuItem>
                {canRollback && <DropdownMenuItem onClick={() => setRollbackOpen(true)} data-testid="action-rollback-stage"><ChevronsDown className="w-3.5 h-3.5 mr-2" /> Rollback Stage</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <StageStepper current={project.workflow_stage || summary.current_stage} stages={STAGES} />

        {/* Sub-tabs */}
        <div className="flex items-center gap-6 px-6 border-b border-border/50 overflow-x-auto">
          {SUB_TABS.map(t => (
            <button key={t} onClick={() => setSubTab(t)}
              className={`py-3 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${subTab===t ? "border-orange-500 text-foreground font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`subtab-${t.toLowerCase().replace(/\s/g,"-")}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {subTab === "Scope Update" && (
        <>
          {/* Metrics + Chart card */}
          <div className="rounded-2xl bg-card border border-border/60 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Scope Progress:</div>
                    <div className="font-display text-2xl font-semibold mt-0.5" data-testid="scope-progress">{summary.scope_progress}%</div>
                  </div>
                </div>
                <div className="pt-3">
                  <div className="text-xs text-emerald-700">{summary.total_items > 0 ? `${filtered.filter(i=> (i.progress||0) < 100 && (i.progress||0) > 0).length} items in progress` : "0 items updated today"}</div>
                  <div className="mt-4">
                    <MetricRow label="Total Items" value={<span>{summary.total_items}  <span className="text-muted-foreground ml-2">₹{Number(summary.total_value).toLocaleString("en-IN",{maximumFractionDigits:2})}</span></span>} />
                    <MetricRow label="Today's Progress" value={<span>0 %  <span className="text-muted-foreground ml-2">₹0</span></span>} />
                    <MetricRow label="Scope Progress" value={<span>{summary.scope_progress}%  <span className="text-muted-foreground ml-2">₹{Number(summary.done_value).toLocaleString("en-IN",{maximumFractionDigits:2})}</span></span>} />
                  </div>
                </div>
              </div>

              <div className="border-l border-border/50 pl-6">
                <div className="text-xs text-muted-foreground">Scope Status:</div>
                <div className="text-orange-600 font-medium mt-0.5" data-testid="scope-status">{scopeStatus}</div>
                <div className="mt-6 space-y-1">
                  <MetricRow label="Expected Start Date:" value={fmtDate(summary.expected_start_date)} />
                  <MetricRow label="Execution Due Date:" value={fmtDate(summary.execution_due_date)} />
                  <MetricRow label="Scope Start Date:" value={fmtDate(summary.scope_start_date)} />
                  <MetricRow label="Scope Completion Date:" value={summary.scope_completion_date ? fmtDate(summary.scope_completion_date) : "—"} muted={!summary.scope_completion_date} />
                </div>
              </div>

              <div className="border-l border-border/50 pl-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm font-medium"><Sparkles className="w-4 h-4 text-primary" /> Scope Progress</div>
                  <Button className="bg-orange-600 hover:bg-orange-700 text-white h-8 text-xs" data-testid="generate-dpr-btn"><Zap className="w-3 h-3 mr-1" />Generate DPR</Button>
                </div>
                <div className="text-[10px] text-muted-foreground text-center mb-1">2024 – 2026</div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={summary.chart_series} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
                    <XAxis dataKey="date" tick={{fontSize: 9}} interval={4} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{fontSize: 9}} />
                    <Tooltip contentStyle={{fontSize: 11}} />
                    <Legend wrapperStyle={{fontSize: 10}} />
                    <Line type="monotone" dataKey="scope" name="Scope Progress" stroke="#10b981" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="days_progress" name="Day's Progress" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Section tabs + filter row + item list */}
          <div className="rounded-2xl bg-card border border-border/60">
            <div className="flex items-center gap-1 px-4 border-b border-border/50 overflow-x-auto">
              <button className="p-2 hover:bg-muted rounded"><Settings2 className="w-4 h-4 text-muted-foreground" /></button>
              <button onClick={() => setActiveSection("all")}
                className={`py-3 px-3 text-sm whitespace-nowrap border-b-2 -mb-px ${activeSection==="all" ? "border-orange-500 font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                All <span className="ml-1.5 text-[10px] bg-muted px-1.5 py-0.5 rounded-full">{items.length}</span>
              </button>
              {sections.map(s => (
                <button key={s.name} onClick={() => setActiveSection(s.name)} data-testid={`section-${s.name.toLowerCase().replace(/\s/g,"-")}`}
                  className={`py-3 px-3 text-sm whitespace-nowrap border-b-2 -mb-px ${activeSection===s.name ? "border-orange-500 font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  {s.name} <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${activeSection===s.name ? "bg-orange-500 text-white" : "bg-muted"}`}>{s.count}</span>
                </button>
              ))}
            </div>

            {/* Filter row */}
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2 p-4 border-b border-border/50 text-xs">
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Item Name & Description</div>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input className="pl-7 h-8 text-xs" placeholder="Search" value={filters.q} onChange={e => setFilters({...filters, q: e.target.value})} />
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Category</div>
                <Select value={filters.category} onValueChange={v => setFilters({...filters, category: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem>{unique("category").map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Status</div>
                <Select value={filters.status} onValueChange={v => setFilters({...filters, status: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem>{unique("status").map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Item Type</div>
                <Select value={filters.item_type} onValueChange={v => setFilters({...filters, item_type: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem>{unique("item_type").map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">UOM</div>
                <Select value={filters.uom} onValueChange={v => setFilters({...filters, uom: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem>{unique("uom").map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Progress</div>
                <Select value={filters.progress} onValueChange={v => setFilters({...filters, progress: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="0">Not started (0%)</SelectItem>
                    <SelectItem value="1-49">Started (1-49%)</SelectItem>
                    <SelectItem value="50-99">Advanced (50-99%)</SelectItem>
                    <SelectItem value="100">Completed (100%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Rows grouped by section */}
            <div className="divide-y divide-border/50">
              {Object.entries(filtered.reduce((acc, it) => {
                const s = it.section || "Unsectioned"; (acc[s] = acc[s] || []).push(it); return acc;
              }, {})).map(([sec, list]) => {
                const secProgress = list.length ? Math.round(list.reduce((a,b)=>a+(b.progress||0),0)/list.length * 10000)/10000 : 0;
                return (
                  <div key={sec}>
                    <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
                      <div className="text-sm font-semibold flex items-center gap-2">{sec} <span className="text-[10px] bg-background border border-border px-1.5 py-0.5 rounded-full">{list.length}</span></div>
                      <div className="text-xs text-muted-foreground">Section Progress: <span className="font-semibold text-foreground">{secProgress}%</span></div>
                    </div>
                    {list.map((it, idx) => (
                      <div key={it.id} className="grid grid-cols-12 gap-3 px-4 py-3 hover:bg-muted/20 items-center text-sm" data-testid={`scope-item-${it.id}`}>
                        <div className="col-span-4 flex gap-3">
                          <div className="text-xs text-muted-foreground w-6 shrink-0 text-right">{idx+1}.</div>
                          <div className="min-w-0">
                            <div className="font-medium truncate">{it.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{it.description}</div>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="text-sm">{it.category}</div>
                          <div className="text-[10px] text-muted-foreground font-mono truncate">{it.code}</div>
                        </div>
                        <div className="col-span-1"><span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[it.status] || "bg-muted"}`}>{it.status}</span></div>
                        <div className="col-span-1 text-sm">{it.item_type || "Nos"}</div>
                        <div className="col-span-1 text-sm">{it.quantity}</div>
                        <div className="col-span-4">
                          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                            <span>Progress Status: <span className="text-foreground font-medium">{it.progress||0} %</span></span>
                            <span>{new Date(it.created_at || Date.now()).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>
                          </div>
                          <input type="range" min={0} max={100} step={5} value={it.progress||0}
                            onChange={(e) => setItems(items.map(x => x.id===it.id ? {...x, progress: Number(e.target.value)} : x))}
                            onMouseUp={(e) => updateProgress(it, Number(e.target.value))}
                            onTouchEnd={(e) => updateProgress(it, Number(e.target.value))}
                            className="w-full accent-emerald-500"
                            data-testid={`scope-progress-${it.id}`} />
                          <div className="flex justify-between text-[9px] text-muted-foreground"><span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
              {filtered.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground">No scope items match your filters.</div>}
            </div>
          </div>
        </>
      )}

      {subTab === "Planning" && <PlanningTab pid={id} project={project} />}
      {subTab === "Timeline" && <TimelineTab pid={id} project={project} user={user} />}
      {subTab === "Snag" && <SnagsTab pid={id} project={project} user={user} />}
      {subTab === "Drawings" && <DrawingsTab pid={id} user={user} backendUrl={BACKEND_URL} />}
      {subTab === "Schedule" && <ScheduleTab pid={id} project={project} />}
      {subTab === "Work List" && <WorkListTab pid={id} user={user} backendUrl={BACKEND_URL} />}

      {subTab === "Progress Report" && <ProgressReportTab pid={id} user={user} backendUrl={BACKEND_URL} />}
      {subTab === "Materials" && <MaterialsTab pid={id} user={user} />}
      {subTab === "Contracts" && <ContractsTab pid={id} user={user} />}
      {subTab === "Financing" && <FinancingTab pid={id} />}

      {/* Advance stage dialog */}
      <Dialog open={advanceOpen} onOpenChange={setAdvanceOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Advance Workflow Stage</DialogTitle><DialogDescription>Move project forward. Only roles authorised for the next stage can advance.</DialogDescription></DialogHeader>
          <div className="text-sm">Current: <span className="font-semibold capitalize">{(project.workflow_stage||"recce").replace(/_/g," ")}</span></div>
          <Textarea placeholder="Handoff note (optional)" rows={3} value={stageNote} onChange={(e) => setStageNote(e.target.value)} data-testid="advance-note-input" />
          <Button onClick={advanceStage} className="bg-primary text-primary-foreground" data-testid="advance-stage-confirm">Advance to next stage</Button>
        </DialogContent>
      </Dialog>

      {/* Rollback dialog */}
      <Dialog open={rollbackOpen} onOpenChange={setRollbackOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rollback Workflow Stage</DialogTitle><DialogDescription>Reverts to the previous stage. Restricted to super_admin/director.</DialogDescription></DialogHeader>
          <Textarea placeholder="Reason for rollback" rows={3} value={stageNote} onChange={(e) => setStageNote(e.target.value)} data-testid="rollback-note-input" />
          <Button onClick={rollbackStage} className="bg-destructive text-destructive-foreground" data-testid="rollback-stage-confirm">Rollback</Button>
        </DialogContent>
      </Dialog>

      {/* Stage history */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Workflow History</DialogTitle><DialogDescription>All stage transitions with timestamps and approvers.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {stageHistory.length === 0 && <div className="text-sm text-muted-foreground">No transitions yet.</div>}
            {stageHistory.map(h => (
              <div key={h.id} className="rounded-lg border border-border/60 p-3" data-testid={`history-${h.id}`}>
                <div className="flex items-center justify-between text-xs">
                  <div className="capitalize">{h.action} · <span className="font-semibold">{h.from_stage?.replace(/_/g," ")}</span> → <span className="font-semibold text-primary">{h.to_stage?.replace(/_/g," ")}</span></div>
                  <div className="text-muted-foreground">{new Date(h.created_at).toLocaleString()}</div>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">By {h.user_name} ({h.user_role})</div>
                {h.note && <div className="text-xs mt-1 italic">"{h.note}"</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
